import type { User } from "@lemmonhq/types";
import type {
  PrintEposJob,
  PrintEposParams,
  PrintEposResult,
} from "@app/vue/providers/bridge-rpc-types";
import { lemmonEnv } from "../../version";

// Kitchen tickets printed by the native app (printEpos) instead of through the
// legacy fetch bridge. Used only when all of these hold:
//   - the app build advertises lemmonEnv.app.capabilities.printEpos >= 1,
//   - the restaurant has feature_flags.native_print on (our rollout switch),
//   - the NativeBridge provider has registered its dispatcher.
// Otherwise printJob takes today's path unchanged.

type Dispatch = (params: PrintEposParams) => Promise<PrintEposResult[]>;

let dispatch: Dispatch | null = null;

/** Called by NativeBridgeProvider with its RPC. */
export const setNativePrintDispatcher = (fn: Dispatch | null) => {
  dispatch = fn;
};

export const supportsNativePrint = (user?: User) =>
  dispatch != null &&
  ((lemmonEnv?.app?.capabilities?.printEpos as number | undefined) ?? 0) >= 1 &&
  user?.restaurant?.feature_flags?.native_print === true;

// The bridge's own request timeout is 1000 s. Native aborts each print after
// 20 s, so a batch should answer well within this.
export const NATIVE_PRINT_TIMEOUT_MS = 45 * 1000;

// Jobs requested in the same tick (an order's tickets, sent from one loop)
// go to native as one batch, so they share one keep-alive.
type Pending = {
  job: PrintEposJob;
  resolve: (result: PrintEposResult) => void;
  reject: (error: Error) => void;
};
let pending: Pending[] = [];
let scheduled = false;

const sendBatch = async () => {
  scheduled = false;
  const batch = pending;
  pending = [];
  if (!dispatch) {
    batch.forEach((p) => p.reject(new Error("native print unavailable")));
    return;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const results = await Promise.race([
      dispatch({ apiBase: location.origin, jobs: batch.map((p) => p.job) }),
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () =>
            reject(
              new Error(
                `TIMEOUT: native print did not answer in ${NATIVE_PRINT_TIMEOUT_MS / 1000}s`,
              ),
            ),
          NATIVE_PRINT_TIMEOUT_MS,
        );
      }),
    ]);
    batch.forEach((p, i) => {
      const result = results?.[i];
      if (result) p.resolve(result);
      else p.reject(new Error("native print returned no result"));
    });
  } catch (e) {
    // No retry, through the legacy bridge or otherwise: native may already
    // have sent the job, and a retry could print it twice.
    const error = e instanceof Error ? e : new Error(String(e));
    batch.forEach((p) => p.reject(error));
  } finally {
    clearTimeout(timer);
  }
};

export const printViaNative = (job: PrintEposJob): Promise<PrintEposResult> =>
  new Promise((resolve, reject) => {
    pending.push({ job, resolve, reject });
    if (!scheduled) {
      scheduled = true;
      setTimeout(() => void sendBatch(), 0);
    }
  });
